import React from 'react';
import { X, Download, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import type { PhotoMetadata } from '../utils/photoMetadata';

interface DebugExifModalProps {
  isOpen: boolean;
  onClose: () => void;
  metadata: PhotoMetadata | null;
}

export function DebugExifModal({ isOpen, onClose, metadata }: DebugExifModalProps) {
  if (!isOpen || !metadata) return null;

  const handleDownloadJson = () => {
    const dataStr = JSON.stringify(metadata, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `exif-debug-${metadata.debugInfo?.fileName || 'photo'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const validation = metadata.debugInfo?.validationDetails;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">EXIF Debug Information</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadJson}
              className="text-blue-600 hover:text-blue-700 transition-colors p-2"
              title="Download JSON"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-700 mb-3">File Information</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">File Name:</span>
                <span className="font-mono text-gray-800">{metadata.debugInfo?.fileName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">File Size:</span>
                <span className="font-mono text-gray-800">
                  {((metadata.debugInfo?.fileSize || 0) / 1024).toFixed(2)} KB
                </span>
              </div>
            </div>
          </div>

          {validation && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-700 mb-3">GPS Validation Breakdown</h3>
              <div className="space-y-2">
                <ValidationRow
                  label="Latitude Present"
                  value={validation.latitudePresent}
                  detail={validation.latitudeValue}
                />
                <ValidationRow
                  label="Longitude Present"
                  value={validation.longitudePresent}
                  detail={validation.longitudeValue}
                />
                <ValidationRow
                  label="Latitude is Number"
                  value={validation.latitudeIsNumber}
                />
                <ValidationRow
                  label="Longitude is Number"
                  value={validation.longitudeIsNumber}
                />
                <ValidationRow
                  label="Latitude in Range (-90 to 90)"
                  value={validation.latitudeInRange}
                />
                <ValidationRow
                  label="Longitude in Range (-180 to 180)"
                  value={validation.longitudeInRange}
                />
              </div>
            </div>
          )}

          <div className="bg-yellow-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-700 mb-3">GPS Fields Found</h3>
            {metadata.debugInfo?.gpsFieldsFound && metadata.debugInfo.gpsFieldsFound.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {metadata.debugInfo.gpsFieldsFound.map((field, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-mono"
                  >
                    {field}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-sm">No GPS fields found</p>
            )}
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-700 mb-3">Extracted GPS Data</h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">Latitude:</span>{' '}
                <span className="font-mono font-semibold text-gray-800">
                  {metadata.latitude !== null ? metadata.latitude.toFixed(6) : 'null'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Longitude:</span>{' '}
                <span className="font-mono font-semibold text-gray-800">
                  {metadata.longitude !== null ? metadata.longitude.toFixed(6) : 'null'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Timestamp:</span>{' '}
                <span className="font-mono font-semibold text-gray-800">
                  {metadata.timestamp || 'null'}
                </span>
              </div>
            </div>
          </div>

          {metadata.debugInfo?.rawGPSData && (
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-700 mb-3">Raw GPS Data</h3>
              <pre className="text-xs font-mono bg-white p-3 rounded border overflow-x-auto">
                {JSON.stringify(metadata.debugInfo.rawGPSData, null, 2)}
              </pre>
            </div>
          )}

          {metadata.debugInfo?.fullExifData && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-700 mb-3">Full EXIF Data</h3>
              <div className="mb-3">
                <p className="text-sm text-gray-600 mb-2">
                  Available EXIF keys ({Object.keys(metadata.debugInfo.fullExifData).length}):
                </p>
                <div className="flex flex-wrap gap-1">
                  {Object.keys(metadata.debugInfo.fullExifData).map((key) => (
                    <span
                      key={key}
                      className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-mono"
                    >
                      {key}
                    </span>
                  ))}
                </div>
              </div>
              <pre className="text-xs font-mono bg-white p-3 rounded border overflow-x-auto max-h-96">
                {JSON.stringify(metadata.debugInfo.fullExifData, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ValidationRow({
  label,
  value,
  detail
}: {
  label: string;
  value: boolean;
  detail?: any;
}) {
  return (
    <div className="flex items-center justify-between bg-white p-2 rounded">
      <div className="flex items-center gap-2">
        {value ? (
          <CheckCircle className="w-4 h-4 text-green-600" />
        ) : (
          <XCircle className="w-4 h-4 text-red-600" />
        )}
        <span className="text-sm text-gray-700">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-semibold ${value ? 'text-green-700' : 'text-red-700'}`}>
          {value ? 'PASS' : 'FAIL'}
        </span>
        {detail !== undefined && detail !== null && (
          <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
            {typeof detail === 'number' ? detail.toFixed(6) : String(detail)}
          </span>
        )}
      </div>
    </div>
  );
}
